
import { Component, Vue } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { emptyToLine, splitThousands, getDateRange } from "@common/src/utils/common";
import dayjs from "dayjs";
import BtnRadio from "@common/src/components/scope/btn-radio/index.vue";
import echarts from "echarts";
import t from "@common/src/i18n/t";
import appCode from "@common/src/utils/app-code";
import { addCurrencyUnit } from "@common/src/utils/tools";
import { InjectFunction } from "@common/src/utils/decorator";

const statement = namespace("statement");
const base = namespace("base");
const management = namespace("management");

@InjectFunction("permitApp")
@Component({
  components: { BtnRadio }
})
export default class Statement extends Vue {
  @statement.Action getCustomerSituationAnalysisModel;
  @base.Action getDictionaryList;
  @management.Action getStoreList;
  addCurrencyUnit = addCurrencyUnit;
  dayjs = dayjs;
  splitThousands = splitThousands;
  emptyToLine = emptyToLine;
  searchForm: any = {
    storeCode: "", // 门店名称
    area: "", // 所在地区
    stateName: "", // 省级名称
    cityName: "", // 市级名称
    districtName: "", // 县级名称
    timeFrameType: "date", // 时间范围类型, date-按日,month-按月
    startTime: "", // 开始时间 格式: 按日为yyyyMMdd, 按月为yyyyMM
    endTime: "", // 结束时间 格式: 按日为yyyyMMdd, 按月为yyyyMM
    businessType: "", // 服务类型，services-服务项目,parts-零配件
    businessCodeList: [] // 服务编码列表
  };
  contentList: any = [];
  pieChartRadio: any = "0";
  barChartRadio: any = "0";

  created() {
    this.init();
  }
  get breadData() {
    return [
      { name: t("v210831.customer-information-report"), path: "/statement/customer-list" },
      { name: t("v210831.customer-analysis") }
    ];
  }
  storeList: any = [];
  get searchList() {
    return [
      {
        label: t("v216_6.statistical-dimension"),
        type: "btn-radio",
        prop: "timeFrameType",
        options: this.dateTypeOptions,
        value: this.searchForm.timeFrameType,
        typeChangeMethod: this.handleDateTypeChange,
        required: true
      },
      {
        if: this.searchForm.timeFrameType === "date",
        label: t("v210831.time-frame"),
        value: [this.searchForm.startTime, this.searchForm.endTime],
        resetValue: this.getDefaultDate("date"),
        type: "daterange",
        prop: "date",
        required: true,
        disabledFuture: true,
        maxDay: 90,
        valueDistribute: ["startTime", "endTime"]
      },
      {
        if: this.searchForm.timeFrameType === "month",
        label: t("v210831.time-frame"),
        value: [this.searchForm.startTime, this.searchForm.endTime],
        resetValue: this.getDefaultDate("month"),
        type: "monthrange",
        prop: "month",
        required: true,
        disabledFuture: true,
        maxMonth: 12,
        valueDistribute: ["startTime", "endTime"]
      },
      {
        if: appCode === "speedbiz-operation",
        label: t("staff.store"),
        type: "select-search",
        value: this.searchForm.storeCode,
        prop: "storeCode",
        selectList: this.storeList,
        searchFun: this.searchStore,
        clearable: false
      },
      {
        if: appCode === "speedbiz-operation",
        label: t("setting.region"),
        type: "area",
        value: this.searchForm.area,
        labels: [],
        prop: "area",
        anyLevel: true,
        valueDistribute: ["stateName", "cityName", "districtName"]
      }
    ].filter(item => item.if !== false);
  }
  searchStore(val = "") {
    this.getStoreList({
      pageNum: 1,
      pageSize: 10,
      ...(this.searchForm.fromList ? { storeCode: this.routeParams.storeCode || "" } : { storeName: val })
    }).then(data => {
      this.storeList = data.data.list
        ? data.data.list.map(item => {
            item.label = item.storeName;
            item.value = item.storeCode;
            return item;
          })
        : [];
    });
  }
  routeParams: any = {};
  init() {
    this.routeParams = this.$route.params;
    if (this.routeParams.fromList === "1") {
      this.searchForm = this.routeParams;
    } else {
      this.handleDateTypeChange(this.searchForm.timeFrameType);
    }
    if (appCode === "speedbiz-operation") {
      this.searchStore("");
    } else {
      this.searchForm.storeCode = "";
    }
    delete this.searchForm.fromList;
    this.getChartData();
  }
  getChartData() {
    this.getCustomerSituationAnalysisModel(this.searchForm).then(data => {
      let {
        payAmountStatistics,
        orderAmountStatistics,
        orderNumberStatistics,
        payAmountMonthlyList,
        orderAmountMonthlyList,
        orderNumberMonthlyList
      } = data.data;
      console.log(data.data, t("v210831.chart"));
      /**
       * @refName
       *  piePayChart  饼图 实收金额   -----payAmountStatistics
       *  pieOrderChart  饼图 订单金额   -----orderAmountStatistics
       *  pieNumberChart  饼图 台次   -----orderNumberStatistics
       *  barPayChart  柱状图 实收金额   -----payAmountMonthlyList
       *  barOrderChart  柱状图 订单金额   -----orderAmountMonthlyList
       *  barNumberChart  柱状图 台次   -----orderNumberMonthlyList
       */
      let piePayChartData = {
        data: [
          {
            name: t("v210831.new-customer-tested"),
            value: payAmountStatistics.newCheckAmount
          },
          {
            name: t("v210831.new-customer-no-test"),
            value: payAmountStatistics.newNoCheckAmount
          },
          {
            name: t("v210831.regular-customers--"),
            value: payAmountStatistics.oldCheckAmount
          },
          {
            name: t("v210831.old-customers--"),
            value: payAmountStatistics.oldNoCheckAmount
          }
        ],
        total: payAmountStatistics.totalAmount,
        text: addCurrencyUnit(t("v210831.total-paid-in"), false, true),
        tip: t("v210831.paid-in-amount")
      };
      this.setChart(piePayChartData, "piePayChart", "pie");
      let pieOrderChartData = {
        data: [
          {
            name: t("v210831.new-customer-tested"),
            value: orderAmountStatistics.newCheckAmount
          },
          {
            name: t("v210831.new-customer-no-test"),
            value: orderAmountStatistics.newNoCheckAmount
          },
          {
            name: t("v210831.regular-customers--"),
            value: orderAmountStatistics.oldCheckAmount
          },
          {
            name: t("v210831.old-customers--"),
            value: orderAmountStatistics.oldNoCheckAmount
          }
        ],
        total: orderAmountStatistics.totalAmount,
        text: addCurrencyUnit(t("v210831.total-order-amount"), false, true),
        tip: t("finance.order-amount")
      };
      this.setChart(pieOrderChartData, "pieOrderChart", "pie");
      let pieNumberChartData = {
        data: [
          {
            name: t("v210831.new-customer-tested"),
            value: orderNumberStatistics.newCheckNumber
          },
          {
            name: t("v210831.new-customer-no-test"),
            value: orderNumberStatistics.newNoCheckNumber
          },
          {
            name: t("v210831.regular-customers--"),
            value: orderNumberStatistics.oldCheckNumber
          },
          {
            name: t("v210831.old-customers--"),
            value: orderNumberStatistics.oldNoCheckNumber
          }
        ],
        total: orderNumberStatistics.totalNumber,
        text: t("v210831.summary-of-sets"),
        tip: t("v210831.number-of-sets"),
        isNumber: true
      };
      this.setChart(pieNumberChartData, "pieNumberChart", "pie");
      let barPayChartData = {
        data1: payAmountMonthlyList.map(v => v.newCustomerAmount),
        data2: payAmountMonthlyList.map(v => v.oldCustomerAmount),
        tip: payAmountMonthlyList,
        unit: this.$localMark.currency,
        text: t("v210831.paid-in-amount")
      };
      this.setChart(barPayChartData, "barPayChart", "bar");
      let barOrderChartData = {
        data1: orderAmountMonthlyList.map(v => v.newCustomerAmount),
        data2: orderAmountMonthlyList.map(v => v.oldCustomerAmount),
        tip: orderAmountMonthlyList,
        unit: this.$localMark.currency,
        text: t("finance.order-amount")
      };
      this.setChart(barOrderChartData, "barOrderChart", "bar");
      let barNumberChartData = {
        data1: orderNumberMonthlyList.map(v => v.newCustomerNumber),
        data2: orderNumberMonthlyList.map(v => v.oldCustomerNumber),
        tip: orderNumberMonthlyList,
        unit: t("v210831.number-of-sets"),
        isNumber: true,
        text: t("v210831.number-of-sets")
      };
      this.setChart(barNumberChartData, "barNumberChart", "bar");
    });
  }
  setChart(data, refName, type) {
    let MyChart = echarts.init(this.$refs[refName]);
    let options = {};
    if (type === "pie") {
      // 饼图pie
      options = {
        color: ["#2278F5", "#599AFA", "#FFC300", "#FF9900"],
        tooltip: {
          trigger: "item",
          formatter: params => {
            return `${params.name}<br/>${data.tip}: ${
              data.isNumber ? params.value : this.$localMark.currencySymbol + splitThousands(params.value)
            }(${params.percent}%)`;
          }
        },
        legend: {
          bottom: "0%",
          left: "center",
          x: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24
        },
        title: {
          show: true,
          text: data.text,
          subtext: splitThousands(data.total),
          top: "43%",
          left: "center",
          textStyle: {
            color: "rgba(0, 0, 0, 0.45)",
            fontWeight: 400,
            fontSize: 12
          },
          subtextStyle: {
            color: "rgba(0, 0, 0, 0.85)",
            fontWeight: 500,
            fontSize: 18
          }
        },
        series: [
          {
            name: "",
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            label: {
              show: false
            },
            data: data.data
          }
        ]
      };
    } else if (type === "bar") {
      // 柱状图bar
      options = {
        color: ["#2278F5", "#FFC300"],
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow"
          },
          formatter: params => {
            let str = `${data.tip[params[0].dataIndex].monthParam}<br/>`;
            params.forEach(item => {
              str += `${item.marker}${item.seriesName}${data.text}: ${
                Number(item.value)
                  ? data.isNumber
                    ? item.value
                    : this.$localMark.currencySymbol + splitThousands(item.value)
                  : "-"
              }<br/>`;
            });
            return str;
          }
        },
        legend: {
          data: [t("v210831.new-customers"), t("v210831.old-customers")],
          bottom: "0%",
          left: "center",
          x: "center",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 24
        },
        xAxis: [
          {
            type: "category",
            axisTick: {
              alignWithLabel: true
            },
            data: data.tip.map(item => item.monthParam)
          }
        ],
        yAxis: [
          {
            name: t("base.unit") + ":" + data.unit,
            axisTick: { show: false },
            type: "value"
          }
        ],
        series: [
          {
            name: t("v210831.new-customers"),
            type: "bar",
            barGap: 0,
            data: data.data1
          },
          {
            name: t("v210831.old-customers"),
            type: "bar",
            data: data.data2
          }
        ]
      };
    }
    MyChart.setOption(options);
    if (type === "pie") {
      const getSelectedTotal = selected => {
        return data.data.reduce((total, item) => {
          if (selected && selected[item.name] === false) {
            return total;
          }
          const value = Number(item.value || 0);
          return total + (isNaN(value) ? 0 : value);
        }, 0);
      };
      MyChart.off("legendselectchanged");
      MyChart.on("legendselectchanged", params => {
        MyChart.setOption({
          title: {
            subtext: splitThousands(getSelectedTotal(params.selected))
          }
        });
      });
    }
    window.addEventListener("resize", () => {
      MyChart.resize();
    });
  }
  handlePieChange(value) {
    this.pieChartRadio = value;
  }
  handleBarChange(value) {
    this.barChartRadio = value;
  }
  handleSearch(data) {
    this.mix_pageNum = 1;
    data.forEach(item => {
      if (item.prop) {
        this.searchForm[item.valueAlias || item.prop] = item.value;
      }
    });
    if (this.permitApp("speedbiz-operation") && !this.searchForm.storeCode) {
      return this.$message.error(t("v210831.please-select-a-store"));
    }
    this.getChartData();
  }
  dateTypeOptions = [
    { label: t("sb_copy.query-by-day"), value: "date" },
    { label: t("sb_copy.query-by-month"), value: "month" }
  ];
  handleDateTypeChange(val) {
    [this.searchForm.startTime, this.searchForm.endTime] = this.getDefaultDate(val);
    this.searchForm.timeFrameType = val;
  }
  getDefaultDate(type: "date" | "month"): Array<string> {
    if (type === "date") {
      return getDateRange("first_day_to_today_of_current_month", "YYYYMMDD");
    } else if (type === "month") {
      return getDateRange("first_month_to_current_month_of_current_year", "YYYYMM");
    } else {
      return [];
    }
  }
}
